import * as React from "react";
import {useContext, useEffect} from "react";
import {QueuedEvent, TransportOrder} from "../../../../../../generated/api";
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {Button, Divider, Dropdown, Header, Label, Menu, Message, Segment} from "semantic-ui-react";
import {OpenFileRequest} from "../../../../../../generated-ftp/api";
import {ConfigContext} from "../../../../context/ConfigContext";
import {queuedEvents} from "../../../converter/ExportSim/input/ExportSimQueuedEventEditor";
import {FileViewer} from "../../../../../util/fileViewer/FileViewer";
import {fetchQueuedEventsForOrder} from "../QueuedEventsList";
import {DownloadButton} from "../../../../../util/react-download-link/DownloadButton";

export interface TransportOrderExportFilesProps {
    order: TransportOrder | null;
    history: any;
}

interface EventToFile {
    eventId: string;
    fileIndex: number;
}

export function TransportOrderExportFiles(props: React.PropsWithChildren<TransportOrderExportFilesProps>) {

    const context = useContext(ConfigContext)

    const [loading, setLoading] = React.useState<boolean>(true);
    const [localQueuedEvents, setLocalQueuedEvents] = React.useState<QueuedEvent[]>([]);
    const [loadingFile, setLoadingFile] = React.useState<boolean>(false);
    const [loadedFileName, setLoadedFileName] = React.useState<string | null>(null);
    const [loadedFile, setLoadedFile] = React.useState<ArrayBuffer | null>(null);
    const [selectedFilesByEvent, setSelectedFilesByEvent] = React.useState<Record<string, EventToFile>>({});
    const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null);
    const [fileError, setFileError] = React.useState<string | null>(null);

    useEffect(() => {
        loadQueuedEvents().then()
    }, [props.order]);

    async function loadQueuedEvents() {
        if (!props.order) {
            setLocalQueuedEvents([]);
            setSelectedEventId(null);
            setSelectedFilesByEvent({});
            setLoadedFile(null);
            setLoadedFileName(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            let events = (await fetchQueuedEventsForOrder(props.order)).events

            let eventsWithFiles = events.filter(e => (e.processingInfo?.exportedFiles?.length ?? 0) > 0)
            if (eventsWithFiles.length == 0) {
                setLocalQueuedEvents([]);
                setSelectedEventId(null);
                setSelectedFilesByEvent({});
                setLoadedFile(null);
                setLoadedFileName(null);
                setLoadingFile(false);
                setFileError(null);
                return;
            }
            setLocalQueuedEvents(eventsWithFiles);

            let firstEvent = eventsWithFiles[0]
            let companyId = firstEvent.companyId ?? context.companyId
            let firstFile = firstEvent.processingInfo?.exportedFiles?.[0] || null;
            if (firstFile && firstEvent._id) {
                setSelectedEventId(firstEvent._id);
                setSelectedFilesByEvent({
                    [firstEvent._id]: {
                        eventId: firstEvent._id,
                        fileIndex: 0
                    }
                });
                await loadFile(firstFile, companyId);
            }
        } finally {
            setLoading(false)
        }


    }

    if (props.order == null) {
        return <span>Please select a transportOrder</span>;
    }

    if (loading) {
        return <span>Loading files...</span>;
    }

    function getSelectedEvent(): QueuedEvent | null {
        if (!selectedEventId) {
            return null;
        }
        return localQueuedEvents.find(event => event._id === selectedEventId) || null;
    }

    function getSelectedFileForEvent(event: QueuedEvent): string | null {
        if (!event._id) {
            return null;
        }
        let selected = selectedFilesByEvent[event._id];
        let selectedIndex = selected?.fileIndex ?? 0;
        return event.processingInfo?.exportedFiles?.[selectedIndex] || null;
    }

    async function selectEvent(event: QueuedEvent) {
        if (!event._id) {
            return;
        }

        setSelectedEventId(event._id);
        const files = event.processingInfo?.exportedFiles || [];
        if (files.length === 0) {
            setLoadedFile(null);
            setLoadedFileName(null);
            setLoadingFile(false);
            setFileError(null);
            return;
        }

        const currentSelection = selectedFilesByEvent[event._id];
        const fileIndex = currentSelection && currentSelection.fileIndex < files.length ? currentSelection.fileIndex : 0;

        setSelectedFilesByEvent((prevState) => ({
            ...prevState,
            [event._id!]: {
                eventId: event._id!,
                fileIndex
            }
        }));

        await loadFile(files[fileIndex], event.companyId ?? context.companyId);
    }

    async function selectFileForEvent(event: QueuedEvent, fileIndex: number) {
        if (!event._id) {
            return;
        }
        const files = event.processingInfo?.exportedFiles || [];
        if (files.length === 0 || fileIndex < 0 || fileIndex >= files.length) {
            return;
        }

        setSelectedFilesByEvent((prevState) => ({
            ...prevState,
            [event._id!]: {
                eventId: event._id!,
                fileIndex
            }
        }));

        await loadFile(files[fileIndex], event.companyId ?? context.companyId);
    }

    function openEventInExportSim(event: QueuedEvent) {
        queuedEvents.value = [event]
        context.setSingleTabSession("exportSimPreEditTab", 1, null, null)
        // navigate to /convPage
        let url = "/" + context.companyId + "/convPage"
        props.history.push(url)
    }

    function renderFile() {
        if (loadingFile) {
            return <div>Loading file...</div>;
        }

        if (fileError) {
            return <Message negative>{fileError}</Message>;
        }

        if (loadedFile == null) {
            return <Message info>Please select a file to preview</Message>;
        }

        return <FileViewer
            fileBuffer={loadedFile}
            fileName={loadedFileName || "unknown"}
            displayName={false}
        />

    }

    async function loadFile(path: string, companyId: string) {
        setLoadingFile(true);
        setFileError(null);

        let request = {} as OpenFileRequest
        request.fileName = path.split("/").pop();
        let auth = backend.withTokenAuthHeader(authentication.token);
        let pathWithoutFileName = path.split("/").slice(0, -1).join("/");
        let selectedCompanyId = companyId || context.companyId;
        let pathParameter = "?path=" + encodeURIComponent(pathWithoutFileName) + "&companyId=" + encodeURIComponent(selectedCompanyId);

        try {
            let file = await fetch('/ftp/openFile' + pathParameter, {
                method: 'POST',
                headers: {
                    "Authorization": auth.headers.Authorization.toString(),
                    "Content-Type": "application/json; charset=utf-8"
                },
                body: JSON.stringify(request)
            })

            if (!file.ok) {
                throw new Error("Unable to open file: " + file.status + " " + file.statusText);
            }

            let blob = await file.blob()
            setLoadedFile(await blob.arrayBuffer())
            setLoadedFileName(request.fileName);
        } catch (error) {
            console.error(error);
            setLoadedFile(null);
            setLoadedFileName(null);
            setFileError("Unable to load selected file");
        } finally {
            setLoadingFile(false);
        }
    }

    const selectedEvent = getSelectedEvent();
    const selectedFilePath = selectedEvent ? getSelectedFileForEvent(selectedEvent) : null;
    const selectedFileIndex = selectedEvent && selectedEvent._id
        ? (selectedFilesByEvent[selectedEvent._id]?.fileIndex ?? 0)
        : 0;
    const totalExportedFiles = localQueuedEvents.reduce((total, event) => total + (event.processingInfo?.exportedFiles?.length ?? 0), 0);
    const selectedEventFileNames = (selectedEvent?.processingInfo?.exportedFiles || []).map((filePath) => filePath.split("/").pop() || filePath);
    const longestFileNameLength = selectedEventFileNames.reduce((maxLength, fileName) => Math.max(maxLength, fileName.length), 0);
    const dropdownMinWidthCh = Math.max(24, longestFileNameLength + 4);

    return <div className={"flex1"} style={{minHeight: 0, overflow: "hidden"}}>
        <div style={{display: "flex", alignItems: "center", gap: 12, flexShrink: 0, marginBottom: 8}}>
            <div style={{flex: 1, minWidth: 0, overflowX: "auto"}}>
                {localQueuedEvents.length > 0 && (
                    <Menu tabular style={{margin: 0, boxShadow: "none"}}>
                        {localQueuedEvents.map((event, index) => (
                            <Menu.Item
                                key={event._id || index}
                                active={event._id === selectedEventId}
                                onClick={() => {
                                    selectEvent(event).then();
                                }}
                            >
                                Event {index + 1} ({event.processingInfo?.exportedFiles?.length || 0})
                            </Menu.Item>
                        ))}
                    </Menu>
                )}
            </div>
            <div style={{display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end"}}>
                <Label>Events with files: {localQueuedEvents.length}</Label>
                <Label>Exported files: {totalExportedFiles}</Label>
            </div>
        </div>

        {localQueuedEvents.length === 0 && (
            <Message info style={{flexShrink: 0}}>No queuedEvents containing any files were found for this TransportOrder</Message>
        )}

        {localQueuedEvents.length > 0 && (
            <div style={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0}}>
                <Segment attached={"bottom"} style={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "visible", borderTop: "none"}}>
                    {!selectedEvent && <Message info style={{flexShrink: 0}}>Select an event tab to preview exported files</Message>}
                    {selectedEvent && <>
                        <div style={{display: "flex", alignItems: "center", gap: 12, flexShrink: 0, flexWrap: "wrap"}}>
                            <Header as={"h4"} style={{margin: 0}}>Event: {selectedEvent._id}</Header>
                            <span>{selectedEvent.eventType} - {selectedEvent.queueType}</span>
                            <Button
                                id={(selectedEvent._id || "event") + "_exportSimButton"}
                                style={{marginLeft: "auto"}}
                                onClick={() => openEventInExportSim(selectedEvent)}
                            >
                                open in ExportSim
                            </Button>
                        </div>

                        <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", flexShrink: 0, position: "relative", zIndex: 20}}>
                            <Dropdown
                                placeholder="Datei auswählen"
                                selection
                                style={{minWidth: `${dropdownMinWidthCh}ch`}}
                                options={selectedEvent.processingInfo?.exportedFiles?.map((filePath: string, fileIndex: number) => ({
                                    key: fileIndex,
                                    text: filePath.split("/").pop() || filePath,
                                    value: fileIndex
                                })) || []}
                                value={selectedFileIndex}
                                onChange={async (_, data) => {
                                    await selectFileForEvent(selectedEvent, Number(data.value));
                                }}
                            />

                            <span>{selectedEvent.processingInfo?.exportedFiles?.length || 0} exported files</span>

                            {selectedFilePath && loadedFile && (
                                <DownloadButton
                                    filename={selectedFilePath.split("/").pop() || selectedFilePath}
                                    exportFile={loadedFile}
                                />
                            )}
                        </div>

                        <Divider style={{flexShrink: 0}}/>
                        <div style={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", position: "relative", zIndex: 1}}>
                            {renderFile()}
                        </div>
                    </>}
                </Segment>
            </div>
        )}
    </div>;
}